import React from "react";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiBook } from "react-icons/fi";
import { participantApi } from "../../../api/participant";
import Loader from "../../../components/loader/Loader";

const pickArray = (response) => response?.data?.courses || [];

const pickInfo = (response) => response?.data || {};
const pickActionPlans = (response) => response?.data || response?.resourceData || response || [];

const SELECTED_COURSE_KEY = "participant:selectedCourse";

const percentFromPath = (info, courses) => {
  if (typeof info?.completionPercentage === "number") return info.completionPercentage;
  if (typeof info?.progressPercentage === "number") return info.progressPercentage;
  const totalCourses = courses.length || 1;
  const completedCourses = courses.filter((course) => {
    const status = String(course?.status || "").toLowerCase();
    return status === "completed";
  }).length;
  return Math.min(100, Math.round((completedCourses / totalCourses) * 100));
};

const LearningPathDetail = () => {
  const navigate = useNavigate();
  const { trackerId, learningPathId } = useParams();

  const { data, isLoading } = useQuery(
    ["participant-learning-path-detail", trackerId, learningPathId],
    () => participantApi.getLearningPathDetail({ trackerId, learningPathId }),
    { enabled: Boolean(trackerId && learningPathId), staleTime: 60000 }
  );

  const { data: actionPlansRes } = useQuery(
    ["participant-action-plans", trackerId, learningPathId],
    participantApi.getActionPlans,
    { staleTime: 60000 }
  );

  const info = pickInfo(data) || {};
  const courses = Array.isArray(pickArray(data)) ? pickArray(data) : [];
  const actionPlans = Array.isArray(pickActionPlans(actionPlansRes)) ? pickActionPlans(actionPlansRes) : [];
  const matchedActionPlan =
    actionPlans.find((item) => {
      const itemTrackerId = item?.trackerId || item?.cohortParticipantId || item?.participantTrackerId;
      const itemLearningPathId = item?.learningPathId || item?.journeyId;
      return String(itemTrackerId || "") === String(trackerId || "") &&
        String(itemLearningPathId || "") === String(learningPathId || "");
    }) || null;
  const progress = percentFromPath(info, courses);
  const actionPlan = matchedActionPlan || info?.resourceData?.actionPlan || info?.actionPlan || null;

  return (
    <div>
      <button
        type="button"
        onClick={() => navigate("/participant/courses-and-learning-path")}
        className="inline-flex items-center gap-2 text-sm text-[#0A3059] mb-4"
      >
        <FiArrowLeft /> Back
      </button>

      <h1 className="text-xl font-semibold text-[#0A3059] mb-2">{info?.learningPathName || "Learning Path"}</h1>
      <p className="text-sm text-gray-600 mb-2">Status: <span className="capitalize">{String(info?.status || "not_started").replaceAll("_", " ")}</span></p>
      <p className="text-sm text-gray-600 mb-3">Browse courses in this learning path.</p>

      <div className="bg-white border border-gray-100 rounded-xl p-3 mb-4 max-w-[900px]">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-gray-600">Learning Path Progress</p>
          <p className="text-xs font-semibold text-[#0A3059]">{progress}%</p>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-gray-200 overflow-hidden">
          <div className="h-1.5 bg-[#8CB33E]" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {actionPlan ? (
        <div className="bg-white border border-gray-100 rounded-xl p-3 mb-5 max-w-[900px]">
          <p className="text-sm font-semibold text-[#0A3059]">Action Plan</p>
          <p className="text-[11px] text-gray-500 mt-1">
            Create goals and submit achievements tied to this learning path.
          </p>
          <button
            type="button"
            onClick={() =>
              navigate(
                `/participant/achievements?actionPlanId=${encodeURIComponent(String(actionPlan?.actionPlanId || actionPlan?.id || ""))}&trackerId=${encodeURIComponent(
                  String(trackerId || "")
                )}&learningPathId=${encodeURIComponent(String(learningPathId || ""))}`
              )
            }
            className="mt-3 px-3 py-1.5 rounded-md text-xs text-white bg-[#0A3059] hover:bg-[#082649]"
          >
            Start Action Plan
          </button>
        </div>
      ) : null}

      {isLoading ? (
        <div className="bg-white p-6 rounded-xl border border-gray-100">
          <Loader />
        </div>
      ) : courses.length ? (
        <div className="space-y-3 max-w-[900px]">
          {courses.map((course) => (
            <div key={course?.id || course?.courseId} className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-base font-medium text-gray-800 inline-flex items-center gap-2">
                    <FiBook className="text-[#0A3059]" />
                    {course?.courseName || "Course"}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-1 capitalize">Status: {String(course?.status || "not_started").replaceAll("_", " ")}</p>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Modules: {course?.completedModules || 0}/{course?.totalModules || 0} | Assessments: {course?.completedAssessments || 0}/{course?.totalAssessments || 0}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const chosenCourseId = course?.id || course?.courseId;
                    sessionStorage.setItem(
                      SELECTED_COURSE_KEY,
                      JSON.stringify({
                        trackerId,
                        learningPathId,
                        courseId: chosenCourseId,
                        courseName: course?.courseName || "Course",
                      })
                    );
                    navigate(
                      `/participant/courses-and-learning-path/${trackerId}/${learningPathId}/courses/${chosenCourseId}`
                    );
                  }}
                  className="px-3 py-1.5 rounded-md text-white bg-[#0A3059] hover:bg-[#082649] text-xs"
                >
                  View Modules
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white p-6 rounded-xl border border-gray-100 text-sm text-gray-500">
          No courses found for this learning path.
        </div>
      )}
    </div>
  );
};

export default LearningPathDetail;
